import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { createSSEParser }              from "../lib/assistant-stream-parser";
import { useAssistantStreaming }        from "./useAssistantStreaming";
import { useKeviaTier }                from "./useKeviaTier";
import { useKeviaContext }             from "../contexts/KeviaContext";
import { orchestrateKevia }               from "../lib/kevia-core/orchestrateKevia";
import { processCognitiveMessage }        from "../lib/kevia-core/runtimeCognitiveLayer";
import { getLocalResponse }            from "../lib/kevia-core/localResponder";
import { saveConversationSummary, pruneMemoryByConversation } from "../lib/kevia-memory";
import { loadOperationalSnapshot, invalidateOperationalSnapshot, clearOperationalSnapshotCache } from "../lib/kevia-core/operationalContext";
import { onKeviaEvent, emitKeviaEvent, KEVIA_EVENTS } from "../lib/kevia-core/context-engine";
import { resolveEntityLiveContext, invalidateEntityCache } from "../lib/kevia-core/context-engine";
import { resolveKeviaAction }          from "../lib/kevia-actions";
import { generateConvTitle, makeConversation } from "../lib/kevia-conversations";
import { pushRuntimeEvent, RT_EVENTS }         from "../lib/kevia-runtime";
import { clearMemory as clearContextMemory }    from "../lib/kevia-core/contextMemoryEngine";
import { trackConversationEntity, resolveConversationEntity, clearConversationEntity } from "../lib/kevia-core/conversationEntityTracker";
import { resolveMutationIntent, getMissingFieldPrompt } from "../lib/kevia-mutations/resolveMutationIntent";
import { executeMutation }                      from "../lib/kevia-mutations/executeMutation";
import { resolveWorkflowIntent }               from "../lib/kevia-workflows/resolveWorkflowIntent";
import { buildWorkflowMemory }                 from "../lib/kevia-workflows/workflowMemory";
import {
  createWorkflow,
  advanceWorkflowStep,
  completeWorkflow,
  pauseWorkflow,
  getOrCreateWorkflow,
} from "../lib/kevia-workflows/workflowRuntime";
import { clearRenderState }                    from "../lib/kevia-workflows/renderPriorityResolver";
import { resetProactiveFatigue }               from "../lib/kevia-workflows/safeProactiveResolver";
import { invalidateDigest }                    from "../lib/kevia-workflows/operationalDigest";
import { resetRecommendations }                from "../lib/kevia-workflows/softRecommendations";
import { clearContinuityState }                from "../lib/kevia-runtime/conversationContinuity";
import { recordJourneyEntry }                  from "../lib/kevia-continuity/spiritualJourneyMemory";
import { recordMilestone, detectImpliedMilestone } from "../lib/kevia-continuity/spiritualMilestoneTracker";
import { isSessionSaturated }                  from "../lib/kevia-runtime/humanRhythm";
import { getRecoveryMessage }                  from "../lib/kevia-runtime/runtimeRecovery";
import { selectContinuityRecall }              from "../lib/kevia-memory/continuityRecallSelector";
import { updateFromConversation }              from "../lib/kevia-memory/relationalMemoryEngine";
import { supabase }                           from "../lib/supabase";

// Navigation chips surfaced when a data tool executes
const DATA_TOOL_NAV = {
  get_today_visits:            { label: "Ver visitas hoy",       target: "/visits" },
  get_upcoming_events:         { label: "Ver eventos",           target: "/agenda" },
  get_family_overview:         { label: "Ver familia",           target: "/families" },
  get_recent_prayers:          { label: "Ver oraciones",         target: "/prayers" },
  get_people_needing_followup: { label: "Ver personas",          target: "/families" },
  get_church_activity_summary: { label: "Ver iglesias",          target: "/churches" },
  get_dashboard_snapshot:      { label: "Ver inicio",            target: "/" },
  get_next_visit:              { label: "Ver próxima visita",    target: "/visits" },
  get_upcoming_visits:         { label: "Ver visitas pendientes", target: "/visits" },
  get_week_schedule:           { label: "Ver agenda semanal",    target: "/agenda" },
  get_visit_timeline:          { label: "Ver historial",         target: "/visits" },
};

const BACKEND_URL     = process.env.REACT_APP_BACKEND_URL ?? "";
const STREAM_ENDPOINT = `${BACKEND_URL}/api/pastoral-assistant/stream`;
const STORAGE_KEY     = "kevia_history";
const MAX_CONVS       = 20;
const MAX_MSGS        = 100;
const STREAM_TIMEOUT  = 25000;
const IS_DEV          = process.env.NODE_ENV === "development";


/* =====================================================
   STORAGE HELPERS
===================================================== */

function loadHistory() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { conversations: [], activeId: null };
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed?.conversations)) return { conversations: [], activeId: null };
    return { conversations: parsed.conversations, activeId: parsed.activeId ?? null };
  } catch {
    return { conversations: [], activeId: null };
  }
}

function saveHistory(conversations, activeId) {
  const serialize = (convs) => JSON.stringify({ conversations: convs, activeId });
  try {
    localStorage.setItem(STORAGE_KEY, serialize(conversations));
  } catch {
    // Quota exceeded — retry with oldest half dropped, then give up
    try {
      const half = conversations.slice(0, Math.max(1, Math.floor(conversations.length / 2)));
      localStorage.setItem(STORAGE_KEY, serialize(half));
    } catch {
      // Private browsing or full quota — silent
    }
  }
}

/* =====================================================
   UTILITIES
===================================================== */

function makeId(prefix = "id") {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

/* =====================================================
   HOOK
===================================================== */

export function useKeviaChat() {
  const initial            = useMemo(loadHistory, []);
  const { tier }                              = useKeviaTier();
  const { appScope, entityContext }           = useKeviaContext();

  const [conversations, setConversations] = useState(initial.conversations);
  const [activeId,      setActiveId]      = useState(() => {
    const { conversations: convs, activeId: aid } = initial;
    if (aid && convs.find((c) => c.id === aid)) return aid;
    return convs[0]?.id ?? null;
  });
  const [loading, setLoading] = useState(false);

  // FASE 21 — pending mutation awaiting user confirmation
  // { toolId, payload, entityName, messageId }
  const [pendingAction, setPendingAction] = useState(null);
  const [mutationLoading, setMutationLoading] = useState(false);

  const conversationsRef      = useRef(conversations);
  const activeIdRef           = useRef(activeId);
  const isMountedRef          = useRef(true);
  const controllerRef         = useRef(null);
  const wasAbortedRef         = useRef(false);
  const lastEntityContextRef  = useRef(null);
  const pendingActionRef      = useRef(null);

  // Operational snapshot — ambient pastoral context loaded on Kevia open.
  // Refreshes every 5 min so it stays current throughout a session.
  const [opSnapshot,    setOpSnapshot]    = useState(null);
  const opSnapshotRef = useRef(null);

  // Entity enrichment — live data for the currently active entity (family/person/church).
  // Loaded whenever entityContext.id/type changes; merged with opSnapshot before LLM call.
  const [entityEnrichment, setEntityEnrichment] = useState(null);
  const entityEnrichmentRef = useRef(null);

  useEffect(() => { conversationsRef.current    = conversations;    }, [conversations]);
  useEffect(() => { activeIdRef.current         = activeId;         }, [activeId]);
  useEffect(() => { pendingActionRef.current    = pendingAction;    }, [pendingAction]);
  useEffect(() => { opSnapshotRef.current       = opSnapshot;       }, [opSnapshot]);
  useEffect(() => { entityEnrichmentRef.current = entityEnrichment; }, [entityEnrichment]);

  // Load operational snapshot whenever the user's scope is available.
  // Uses a ref-based scope key to avoid re-running on unrelated appScope re-renders.
  const scopeKeyRef = useRef(null);
  useEffect(() => {
    const newKey = `${appScope?.district_id ?? ""}:${appScope?.church_id ?? ""}`;
    if (!newKey || newKey === ":") return;
    if (newKey === scopeKeyRef.current) return;
    scopeKeyRef.current = newKey;

    loadOperationalSnapshot(appScope).then((snap) => {
      if (isMountedRef.current) setOpSnapshot(snap);
    });

    const intervalId = setInterval(() => {
      loadOperationalSnapshot(appScope).then((snap) => {
        if (isMountedRef.current) setOpSnapshot(snap);
      });
    }, 5 * 60 * 1000);

    return () => clearInterval(intervalId);
  // appScope object changes identity on re-render — depend on stable IDs only
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appScope?.district_id, appScope?.church_id, appScope?.role]);

  // Load entity enrichment whenever active entity changes.
  // Only fires for Plus tier (LLM-capable) and when an entity is registered.
  useEffect(() => {
    if (!entityContext?.id || !tier?.can?.llm) {
      setEntityEnrichment(null);
      return;
    }
    resolveEntityLiveContext(entityContext, appScope).then((enrichment) => {
      if (isMountedRef.current) setEntityEnrichment(enrichment);
    });
    return () => setEntityEnrichment(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityContext?.id, entityContext?.type, tier?.can?.llm]);

  // Subscribe to liveEventBridge — instant invalidation on mutations.
  // Clears entity cache and reloads the operational snapshot.
  useEffect(() => {
    const unsub = onKeviaEvent((event) => {
      if (event.type === KEVIA_EVENTS.MUTATION_SUCCESS && isMountedRef.current) {
        // Invalidate entity-level enrichment cache
        if (entityContext) invalidateEntityCache(entityContext);
        // Clear + reload operational snapshot (mutation changed scope-level counts)
        clearOperationalSnapshotCache();
        loadOperationalSnapshot(appScope).then((snap) => {
          if (isMountedRef.current) setOpSnapshot(snap);
        });
      }
    });
    return unsub;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appScope?.district_id, appScope?.church_id]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      controllerRef.current?.abort();
      // 23.10 / 24 — cleanup session-scoped state on unmount
      clearRenderState();
      resetProactiveFatigue();
      clearContinuityState();
      resetRecommendations();
    };
  }, []);

  /* =====================================================
     STREAMING STATE
  ===================================================== */
  const {
    streamingContent,
    isStreaming,
    startStream,
    appendChunk,
    finalizeStream,
    cancelStream,
  } = useAssistantStreaming();

  useEffect(() => {
    return () => cancelStream();
  }, [cancelStream]);

  const stopStream = useCallback(() => {
    wasAbortedRef.current = true;
    controllerRef.current?.abort();
    cancelStream();
  }, [cancelStream]);

  /* =====================================================
     AUTO-SAVE
  ===================================================== */
  useEffect(() => {
    saveHistory(conversations, activeId);
  }, [conversations, activeId]);

  /* =====================================================
     DERIVED: active messages
  ===================================================== */
  const messages = useMemo(
    () => conversations.find((c) => c.id === activeId)?.messages ?? [],
    [conversations, activeId],
  );

  /* =====================================================
     CONVERSATION MANAGEMENT
  ===================================================== */

  const createConversation = useCallback(() => {
    const id   = makeId("conv");
    const conv = makeConversation({ id, tier: tier?.id ?? "basic" });
    setConversations((prev) => [conv, ...prev].slice(0, MAX_CONVS));
    setActiveId(id);
    pushRuntimeEvent({
      type:   RT_EVENTS.START_CONVERSATION,
      module: appScope?.module,
      metadata: { conversationId: id, tier: tier?.id ?? "basic" },
    });
    return id;
  }, [tier, appScope?.module]);

  const selectConversation = useCallback((id) => {
    setActiveId(id);
  }, []);

  const deleteConversation = useCallback((id) => {
    pruneMemoryByConversation(id);
    clearConversationEntity(id);
    clearContextMemory(id);
    setConversations((prev) => {
      const next = prev.filter((c) => c.id !== id);
      setActiveId((prevActive) => {
        if (prevActive !== id) return prevActive;
        return next[0]?.id ?? null;
      });
      return next;
    });
  }, []);

  /* =====================================================
     SEND MESSAGE (streaming)
     entityContext: full context object from composeKeviaContext(),
                   or null when called from the standalone KeviaChat page.
  ===================================================== */

  const sendMessage = useCallback(
    async (text, entityContext = null) => {
      if (!text?.trim() || loading) return;
      const trimmed = text.trim();
      lastEntityContextRef.current = entityContext;

      // Reset abort sentinel so a completed-stream abort can't ghost the next error catch
      wasAbortedRef.current = false;

      // Dismiss any stale pending action before staging a new message
      if (pendingActionRef.current) {
        setPendingAction(null);
        pendingActionRef.current = null;
      }

      // Resolve current conversation (create one if needed)
      let currentId = activeIdRef.current;
      const existingConv = conversationsRef.current.find((c) => c.id === currentId);
      if (!currentId || !existingConv) {
        currentId = makeId("conv");
        const newConv = makeConversation({
          id:            currentId,
          text:          trimmed,
          tier:          tier?.id ?? "basic",
          entityContext,
        });
        setConversations((prev) => [newConv, ...prev].slice(0, MAX_CONVS));
        setActiveId(currentId);
      }

      // Snapshot current messages for history prefix (before appending user msg)
      const currentMsgs = conversationsRef.current.find((c) => c.id === currentId)?.messages ?? [];

      // Resolve frontend actions before the LLM responds
      const baseActions = resolveKeviaAction(trimmed);

      // Append user message
      const userMsg = { id: makeId("msg"), role: "user", content: trimmed, ts: Date.now() };
      setConversations((prev) =>
        prev.map((c) => {
          if (c.id !== currentId) return c;
          const isFirst = c.messages.length === 0;
          return {
            ...c,
            title:     isFirst ? generateConvTitle(trimmed) : c.title,
            updatedAt: Date.now(),
            messages:  [...c.messages, userMsg].slice(-MAX_MSGS),
          };
        }),
      );

      // Emit send-message cognition event (metadata only — no content stored)
      pushRuntimeEvent({
        type:   RT_EVENTS.SEND_MESSAGE,
        module: appScope?.module,
        metadata: {
          messageLength:  trimmed.length,
          conversationId: currentId,
          tier:           tier?.id ?? "basic",
        },
      });

      setLoading(true);

      // Entity tracking — update memory when an explicit entity is present,
      // then resolve for pronoun-based follow-up questions ("su última visita")
      if (entityContext?.id) {
        trackConversationEntity(currentId, entityContext);
      }
      const resolvedEntity = entityContext ?? resolveConversationEntity(currentId, trimmed) ?? null;

      // ── Cognitive data pipeline ──────────────────────────────────
      // Handles intent, tool planning, execution, synthesis and proactive context.
      const cogResult = await processCognitiveMessage({
        text:           trimmed,
        conversationId: currentId,
        entityContext:  resolvedEntity,
        appScope,
        tier,
      });

      const {
        dataContext,
        synthesisContext:    synthesisCtx,
        proactiveContext,
        relationshipContext: _relCtx,
        trajectoryContext:   _trajCtx,
        riskSignals:         _riskSignals,
        autonomyContext:     _autonomyCtx,
        autonomyDebug:       _autonomyDbg,
        executedToolId,
        toolsExecuted,
        intent:             _cogIntent,
        temporalIntent:     _cogTemporal,
        insights:           _cogInsights,
        ministryPressure:   _cogPressure,
        sessionLoad:        _cogSessionLoad,
        bibleQuery:         _cogBibleQuery,
        denominationalPlan: _cogDenomPlan,
      } = cogResult;

      if (IS_DEV) {
        window.__KEVIA_DEBUG__ = {
          intent:          _cogIntent,
          temporal:        _cogTemporal,
          tool:            executedToolId,
          toolsRan:        toolsExecuted,
          rows:            0,
          synthesis:       synthesisCtx ? "✓" : null,
          proactive:       proactiveContext ? "✓" : null,
          relationship:    _relCtx      ? "✓" : null,
          trajectory:      _trajCtx     ? "✓" : null,
          riskSignals:     _riskSignals  ?? 0,
          autonomy:        _autonomyCtx  ? "✓" : null,
          priorities:      _autonomyDbg?.priorities    ?? 0,
          followups:       _autonomyDbg?.followups     ?? 0,
          ministryLoad:    _autonomyDbg?.load          ?? "low",
          continuity:      _autonomyDbg?.continuity    ?? false,
          openProcesses:   _autonomyDbg?.openProcesses ?? 0,
          insights:        _cogInsights.length,
          pressure:        _cogPressure,
          module:          appScope?.module,
          scope:           { role: appScope?.role, district_id: appScope?.district_id?.slice(0, 8) },
          entityId:        resolvedEntity?.id?.slice(0, 8) ?? null,
          entityType:      resolvedEntity?.type ?? null,
          ts:              Date.now(),
        };
        console.info("[Kevia/debug]", window.__KEVIA_DEBUG__);
      }

      // FASE 21 — Resolve mutation intent from user text
      // If action detected + all fields present → stage confirm card after LLM responds
      let mutationIntent = null;
      if (trimmed.length > 4) {
        mutationIntent = resolveMutationIntent(trimmed, entityContext, appScope);
        if (mutationIntent) {
          if (IS_DEV) console.info("[Kevia/mutation]", mutationIntent.toolId, "confidence:", mutationIntent.confidence, "missing:", mutationIntent.missingFields);
        }
      }

      // FASE 22 — Resolve workflow intent and act on it (no confirmation needed for reads)
      if (trimmed.length > 4) {
        const wfIntent = resolveWorkflowIntent(trimmed, entityContext);
        if (wfIntent.action !== "none" && wfIntent.confidence >= 0.75) {
          if (IS_DEV) console.info("[Kevia/workflow]", wfIntent.action, wfIntent.type, "confidence:", wfIntent.confidence);
          if (wfIntent.action === "start" && wfIntent.type) {
            const relatedEntities = entityContext?.entityId
              ? [{ type: entityContext.entityType ?? "person", id: entityContext.entityId, name: entityContext.entityName ?? "" }]
              : [];
            getOrCreateWorkflow({ type: wfIntent.type, related_entities: relatedEntities });
            invalidateDigest();
            pushRuntimeEvent({ type: RT_EVENTS.WORKFLOW_STARTED, metadata: { workflowType: wfIntent.type } });
          } else if (wfIntent.action === "complete" && wfIntent.workflowId) {
            completeWorkflow(wfIntent.workflowId);
            invalidateDigest();
            pushRuntimeEvent({ type: RT_EVENTS.WORKFLOW_COMPLETED, metadata: { workflowId: wfIntent.workflowId } });
          } else if (wfIntent.action === "pause" && wfIntent.workflowId) {
            pauseWorkflow(wfIntent.workflowId);
            invalidateDigest();
            pushRuntimeEvent({ type: RT_EVENTS.WORKFLOW_PAUSED, metadata: { workflowId: wfIntent.workflowId } });
          }
        }
      }

      // FASE 22 / 24.7 — Build compact workflow context; suppress when session is saturated
      const workflowContext = isSessionSaturated() ? null : buildWorkflowMemory();

      // R5: Selective continuity recall — surface warm memory fragment when emotionally relevant.
      // Only injected at conversation start (not mid-stream) and suppressed for logistics.
      const continuityRecall = selectContinuityRecall(
        resolvedEntity ? { entityId: resolvedEntity.id, entityType: resolvedEntity.type } : null,
        trimmed,
        currentMsgs.length > 0,
      );

      // Merge entity enrichment + continuity fragment into operational context.
      const operationalContext = [
        opSnapshotRef.current,
        entityEnrichmentRef.current,
        continuityRecall.shouldRecall ? continuityRecall.fragment : null,
      ]
        .filter(Boolean)
        .join("\n") || null;

      // Orchestrate — resolve active tier, build payload, inject context
      const { payload, userPrompt, intent, canLlm, canStream, convMode, riskLevel, discernment: _discernment, formation: _formation, judgment: _judgment, continuity: _continuity, ecclesia: _ecclesia, pastoralState: _pastoralState, refinement: _refinement, intuition: _intuition, humanRhythm: _humanRhythm } = orchestrateKevia({
        text:                trimmed,
        entityContext,
        historyMsgs:         currentMsgs,
        runtimeState:        { isStreaming: false },
        tier,
        appScope,
        dataContext,
        synthesisContext:    synthesisCtx,
        proactiveContext,
        relationshipContext: _relCtx      ?? null,
        trajectoryContext:   _trajCtx     ?? null,
        autonomyContext:     _autonomyCtx ?? null,
        workflowContext,
        operationalContext,
        temporalIntent:      _cogTemporal,
        workflowActive:      !!workflowContext,
        bibleQuery:          _cogBibleQuery ?? null,
        denominationalPlan:  _cogDenomPlan  ?? null,
      });

      if (IS_DEV) {
        if (window.__KEVIA_DEBUG__) {
          window.__KEVIA_DEBUG__.mode         = convMode                        ?? null;
          window.__KEVIA_DEBUG__.riskLevel    = riskLevel                       ?? "none";
          window.__KEVIA_DEBUG__.discernment  = _discernment?.discernmentBlock  ? "✓" : null;
          window.__KEVIA_DEBUG__.depth        = _discernment?.depth             ?? null;
          window.__KEVIA_DEBUG__.pacing       = _discernment?.timing            ?? null;
          window.__KEVIA_DEBUG__.wisdomTone   = _discernment?.wisdomTone        ?? null;
          window.__KEVIA_DEBUG__.questions    = _discernment?.shouldQuestion    ? 1 : 0;
          window.__KEVIA_DEBUG__.empBalance   = _discernment?.empathyBalance    ?? null;
          window.__KEVIA_DEBUG__.formation    = _formation?.formationBlock      ? "✓" : null;
          window.__KEVIA_DEBUG__.presence     = _formation?.presence            ?? null;
          window.__KEVIA_DEBUG__.posture      = _formation?.posture             ?? null;
          window.__KEVIA_DEBUG__.hope         = _formation?.hopeLevel           ?? null;
          window.__KEVIA_DEBUG__.reverence    = _formation?.reverenceLevel      ?? null;
          window.__KEVIA_DEBUG__.judgment     = _judgment?.judgmentBlock        ? "✓" : null;
          window.__KEVIA_DEBUG__.complexity   = _judgment?.complexity           ?? null;
          window.__KEVIA_DEBUG__.tension      = _judgment?.tension?.primary     ?? null;
          window.__KEVIA_DEBUG__.principle    = _judgment?.principle            ?? null;
          window.__KEVIA_DEBUG__.ambiguity    = _judgment?.ambiguityLevel       ?? null;
          window.__KEVIA_DEBUG__.process      = _judgment?.processState         ?? null;
          window.__KEVIA_DEBUG__.continuity   = _continuity?.continuityContext  ? "✓" : null;
          window.__KEVIA_DEBUG__.journey      = _continuity?.journeyArc        ?? null;
          window.__KEVIA_DEBUG__.pattern      = _continuity?.pattern !== "sin_patron" ? _continuity?.pattern : null;
          window.__KEVIA_DEBUG__.recall       = _continuity?.recallMode        ?? null;
          window.__KEVIA_DEBUG__.milestones   = _continuity?.milestoneCount    ?? 0;
          window.__KEVIA_DEBUG__.regression   = _continuity?.regressionState !== "stable" ? _continuity?.regressionState : null;
          window.__KEVIA_DEBUG__.ecclesia     = _ecclesia?.ecclesialContext      ? "✓"   : null;
          window.__KEVIA_DEBUG__.family       = _ecclesia?.familyDynamic !== "ninguno"  ? _ecclesia?.familyDynamic  : null;
          window.__KEVIA_DEBUG__.congregation = _ecclesia?.congregationSignal !== "ninguno" ? _ecclesia?.congregationSignal : null;
          window.__KEVIA_DEBUG__.ecc_load     = _ecclesia?.loadState !== "low"  ? _ecclesia?.loadState  : null;
          window.__KEVIA_DEBUG__.conflict     = _ecclesia?.conflictType !== "ninguno" ? _ecclesia?.conflictType : null;
          window.__KEVIA_DEBUG__.care         = _ecclesia?.carePattern !== "ninguno"  ? _ecclesia?.carePattern  : null;
          window.__KEVIA_DEBUG__.silent       = _ecclesia?.silentTension ?? 0;
          window.__KEVIA_DEBUG__.pastoralState  = _pastoralState?.isActive          ? "✓" : null;
          window.__KEVIA_DEBUG__.ps_exhaustion  = _pastoralState?.exhaustionState !== "estable"       ? _pastoralState?.exhaustionState     : null;
          window.__KEVIA_DEBUG__.ps_fatigue     = _pastoralState?.fatigueType     !== "ninguna"       ? _pastoralState?.fatigueType         : null;
          window.__KEVIA_DEBUG__.ps_isolation   = _pastoralState?.isolationState  !== "acompañado"    ? _pastoralState?.isolationState      : null;
          window.__KEVIA_DEBUG__.ps_responsib   = _pastoralState?.responsibilityState !== "distribuido" ? _pastoralState?.responsibilityState : null;
          window.__KEVIA_DEBUG__.ps_capacity    = _pastoralState?.capacity        !== "stable"        ? _pastoralState?.capacity            : null;
          window.__KEVIA_DEBUG__.ps_escalation  = _pastoralState?.escalation      !== "maintain"      ? _pastoralState?.escalation          : null;
          window.__KEVIA_DEBUG__.session_load   = _cogSessionLoad !== "low" ? _cogSessionLoad : null;
          window.__KEVIA_DEBUG__.refinement     = _refinement?.isActive      ? "✓" : null;
          window.__KEVIA_DEBUG__.r_compression  = _refinement?.compressionRatio > 0 ? `${_refinement.compressionRatio}%` : null;
          window.__KEVIA_DEBUG__.r_signal_count = _refinement?.signalCount   > 0    ? _refinement.signalCount : null;
          window.__KEVIA_DEBUG__.r_dedup        = _refinement?.dedupCount    > 0    ? _refinement.dedupCount  : null;
          window.__KEVIA_DEBUG__.r_simplicity   = _refinement?.simplicityLevel !== "normal"  ? _refinement?.simplicityLevel  : null;
          window.__KEVIA_DEBUG__.r_coherence    = _refinement?.coherenceState  !== "stable"  ? _refinement?.coherenceState   : null;
          window.__KEVIA_DEBUG__.r_load         = _refinement?.loadLevel       !== "low"     ? _refinement?.loadLevel        : null;
          window.__KEVIA_DEBUG__.mem_recall     = continuityRecall.shouldRecall ? continuityRecall.reason : null;
          window.__KEVIA_DEBUG__.intuition      = _intuition?.intuitionContext   ? "✓" : null;
          window.__KEVIA_DEBUG__.dominant       = _intuition?.dominantSignal  !== "simple_presence" ? _intuition?.dominantSignal : null;
          window.__KEVIA_DEBUG__.impulse        = _intuition?.impulse          !== "no_forzar"       ? _intuition?.impulse        : null;
          window.__KEVIA_DEBUG__.minimalism     = _intuition?.minimalismLevel !== "low"              ? _intuition?.minimalismLevel : null;
          window.__KEVIA_DEBUG__.quiet          = _intuition?.shouldQuiet      ? "✓" : null;
          window.__KEVIA_DEBUG__.rhythm         = _humanRhythm?.rhythmContext   ? "✓" : null;
          window.__KEVIA_DEBUG__.rhythm_profile = _humanRhythm?.rhythmProfile !== "functional" ? _humanRhythm?.rhythmProfile : null;
          window.__KEVIA_DEBUG__.understate      = _humanRhythm?.understateMode ? "✓" : null;
          // relationship/trajectory already set above — preserve
        }
        if (IS_DEV) console.info("[Kevia] intent:", intent, "| mode:", convMode, "| risk:", riskLevel, "| rel:", !!_relCtx, "| canLlm:", canLlm, "| tier:", tier?.id);
      }

      // Start streaming UI only for Plus tier (char-by-char animation)
      if (canLlm && canStream) startStream();

      let assembled     = "";
      let abortedByUser = false;

      // ── Local mode: instant response, no streaming animation ─
      if (!canLlm) {
        assembled = getLocalResponse(entityContext, trimmed);
      } else {
        // ── LLM mode: SSE stream ──────────────────────────
        try {
          const controller = new AbortController();
          controllerRef.current = controller;
          const timerId    = setTimeout(() => controller.abort(), STREAM_TIMEOUT);

          let res;
          try {
            if (IS_DEV) console.info("[Kevia] → fetch", STREAM_ENDPOINT);
            const { data: { session } } = await supabase.auth.getSession();
            const token = session?.access_token ?? "";
            res = await fetch(STREAM_ENDPOINT, {
              method:  "POST",
              headers: {
                "Content-Type":  "application/json",
                "Authorization": `Bearer ${token}`,
              },
              body:    JSON.stringify(payload),
              signal:  controller.signal,
            });
          } finally {
            clearTimeout(timerId);
          }

          if (res.ok && res.body) {
            const reader  = res.body.getReader();
            const decoder = new TextDecoder();
            const parser  = createSSEParser();

            while (true) {
              const { done, value } = await reader.read();
              if (done) break;

              const events = parser.push(decoder.decode(value, { stream: true }));
              for (const event of events) {
                if (event.type === "token" && event.data?.chunk) {
                  assembled += event.data.chunk;
                  if (canStream) appendChunk(event.data.chunk);
                } else if (event.type === "done") {
                  if (event.data?.finishReason === "rate_limit") {
                    const resetAt = event.data?.reset_at;
                    const resetTime = resetAt
                      ? ` Kevia estará disponible a las ${new Date(resetAt).toLocaleTimeString("es", { hour: "2-digit", minute: "2-digit" })}.`
                      : "";
                    assembled = `Has alcanzado tu límite de mensajes.${resetTime}`;
                  } else if (event.data?.content) {
                    assembled = event.data.content;
                  }
                }
              }
            }
          } else {
            let body = "";
            try { body = await res.text(); } catch { /* ignore */ }
            if (IS_DEV) console.error("[Kevia] ✗ backend error:", res.status, body.slice(0, 200));
            if (res.status === 429) {
              let resetTime = "";
              try {
                const parsed = JSON.parse(body);
                if (parsed?.detail?.reset_at) {
                  resetTime = ` Kevia estará disponible a las ${new Date(parsed.detail.reset_at).toLocaleTimeString("es", { hour: "2-digit", minute: "2-digit" })}.`;
                }
              } catch { /* ignore */ }
              assembled = `Has alcanzado tu límite de mensajes.${resetTime}`;
            } else if (res.status >= 500) {
              assembled = "Kevia no está disponible en este momento. Intenta más tarde.";
            } else {
              assembled = getLocalResponse(entityContext, trimmed);
            }
            if (canStream) appendChunk(assembled);
          }
        } catch (err) {
          if (wasAbortedRef.current) {
            abortedByUser         = true;
            wasAbortedRef.current = false;
          } else {
            if (IS_DEV) console.error("[Kevia] ✗ stream exception:", err?.name, "-", err?.message);
            if (typeof navigator !== "undefined" && !navigator.onLine) {
              assembled = "Sin conexión. Kevia no está disponible offline.";
            } else {
              assembled = getLocalResponse(entityContext, trimmed);
            }
            if (canStream) appendChunk(assembled);
          }
        }
      }

      if (!isMountedRef.current) return;

      if (abortedByUser && !assembled.trim()) {
        if (isMountedRef.current) {
          finalizeStream();
          setLoading(false);
        }
        return;
      }

      const responseContent = assembled.trim() || getRecoveryMessage("stream");
      const isLocalFallback = !canLlm || (!assembled.trim() && !abortedByUser);

      const assistantMsg = {
        id:               makeId("msg"),
        role:             "assistant",
        content:          responseContent,
        ts:               Date.now(),
        isFallback:       !assembled.trim() && !abortedByUser,
        isPartial:        abortedByUser && !!assembled.trim(),
        isLocal:          isLocalFallback,
        isBlock:          canLlm && !canStream,   // basic tier: block fade-in
        tier:             tier?.id ?? "basic",
        suggestedActions: (() => {
          // FASE 20.11 — merge data-driven nav chip when a tool was executed
          const dataChip = executedToolId ? DATA_TOOL_NAV[executedToolId] : null;
          const merged   = dataChip && !baseActions.find((a) => a.target === dataChip.target)
            ? [...baseActions, dataChip]
            : baseActions;
          return merged.length > 0 ? merged : undefined;
        })(),
      };

      const lastPreview = responseContent.slice(0, 80);

      // FASE 21 — If mutation intent is ready, also append an action_proposal message
      const actionProposalMsg = (
        mutationIntent?.isReadyToConfirm &&
        mutationIntent.confidence >= 0.75 &&
        !abortedByUser
      ) ? {
        id:         makeId("action"),
        role:       "assistant",
        type:       "action_proposal",
        ts:         Date.now(),
        action: {
          toolId:     mutationIntent.toolId,
          payload:    mutationIntent.extractedFields,
          entityName: entityContext?.name ?? null,
        },
      } : null;

      // If missing fields, append a follow-up question instead
      const missingFieldMsg = (
        mutationIntent &&
        !mutationIntent.isReadyToConfirm &&
        mutationIntent.confidence >= 0.70 &&
        mutationIntent.missingFields.length > 0 &&
        !abortedByUser &&
        !canLlm  // only inject for local mode; LLM handles its own recovery
      ) ? {
        id:      makeId("msg"),
        role:    "assistant",
        content: getMissingFieldPrompt(mutationIntent.missingFields[0]),
        ts:      Date.now(),
        isLocal: true,
      } : null;

      setConversations((prev) =>
        prev.map((c) => {
          if (c.id !== currentId) return c;
          const newMsgs = [assistantMsg];
          if (actionProposalMsg) newMsgs.push(actionProposalMsg);
          else if (missingFieldMsg) newMsgs.push(missingFieldMsg);
          return {
            ...c,
            updatedAt:   Date.now(),
            lastMessage: lastPreview,
            messages:    [...c.messages, ...newMsgs].slice(-MAX_MSGS),
          };
        }),
      );

      // Stage the pending action for the confirm card
      if (actionProposalMsg) {
        const staged = {
          toolId:     mutationIntent.toolId,
          payload:    mutationIntent.extractedFields,
          entityName: entityContext?.name ?? null,
          messageId:  actionProposalMsg.id,
        };
        setPendingAction(staged);
        if (IS_DEV && typeof window !== "undefined") window.__KEVIA_PENDING_ACTION__ = staged;
      }

      // Persist conversation summary to pastoral memory when entity context is present
      if (entityContext?.type && entityContext?.id && assembled.trim() && !abortedByUser) {
        const firstSentence = assembled.trim().split(/[.!?\n]/)[0]?.trim() ?? assembled.trim();
        const summary = firstSentence.length > 80 ? firstSentence.slice(0, 77) + "…" : firstSentence;
        const tone    = entityContext?.tone ?? "neutral";
        saveConversationSummary(entityContext.type, entityContext.id, summary, tone, currentId);

        // FASE 9 — Record spiritual journey signal when a meaningful process state was detected
        const ps = _judgment?.processState;
        if (ps && ps !== "apertura" && !abortedByUser) {
          const prevRecord = null;  // coordinator already read it — no need to reload
          recordJourneyEntry(entityContext.id, entityContext.type, entityContext.name ?? "", {
            ts:           Date.now(),
            processState: ps,
            tone,
            intensity:    (_judgment?.complexity === "deeply_complex" || _judgment?.complexity === "delicate") ? "high" : "medium",
            note:         null,
          });

          // Detect and record implied milestones from process state transitions
          const prevState = _continuity?.journeyArc === "recaida_despues_de_restauracion"
            ? "restauración"
            : _continuity?.journeyArc === "estabilidad_despues_de_crisis"
              ? "quebranto"
              : null;
          if (prevState) {
            const implied = detectImpliedMilestone(prevState, ps);
            if (implied) {
              recordMilestone(entityContext.id, entityContext.type, entityContext.name ?? "", { type: implied });
            }
          }
        }

        // R5 — Update emotional memory after conversation (write-back to relationalMemoryEngine)
        // Only fires when a process state was detected — logistics conversations don't write.
        if (ps) {
          updateFromConversation({
            entityId:      entityContext.id,
            entityType:    entityContext.type,
            processState:  ps,
            mood:          tone,
            pastoralState: _pastoralState ?? null,
            milestoneType: _judgment?.complexity === "deeply_complex" ? null : null,
          });
        }
      }

      if (isMountedRef.current) {
        finalizeStream();
        setLoading(false);
      }
    },
    [loading, startStream, appendChunk, finalizeStream, tier, appScope],
  );

  /* =====================================================
     RETRY last failed/partial response
  ===================================================== */

  const retryLast = useCallback(() => {
    if (loading) return;
    pushRuntimeEvent({ type: RT_EVENTS.RETRY_RESPONSE, module: appScope?.module });
    const convId = activeIdRef.current;
    const msgs   = conversationsRef.current.find((c) => c.id === convId)?.messages ?? [];
    if (msgs.length < 2) return;

    const lastMsg = msgs[msgs.length - 1];
    if (lastMsg.role !== "assistant" || (!lastMsg.isFallback && !lastMsg.isPartial)) return;

    const lastUserMsg = [...msgs].slice(0, -1).reverse().find((m) => m.role === "user");
    if (!lastUserMsg) return;

    setConversations((prev) =>
      prev.map((c) =>
        c.id !== convId ? c : { ...c, messages: c.messages.slice(0, -2) },
      ),
    );

    sendMessage(lastUserMsg.content, lastEntityContextRef.current);
  }, [loading, sendMessage, appScope?.module]);

  /* =====================================================
     CLEAR active conversation
  ===================================================== */

  const clearConversation = useCallback(() => {
    const id = activeIdRef.current;
    if (!id) return;
    setConversations((prev) =>
      prev.map((c) => (c.id === id ? { ...c, messages: [], updatedAt: Date.now() } : c)),
    );
  }, []);

  /* =====================================================
     MUTATION CONFIRMATION (FASE 21)
  ===================================================== */

  // Update an action_proposal message's outcome inline
  const _updateProposalOutcome = useCallback((messageId, outcome, outcomeMessage) => {
    setConversations((prev) =>
      prev.map((c) => ({
        ...c,
        messages: c.messages.map((m) =>
          m.id === messageId
            ? { ...m, action: { ...m.action, outcome, outcomeMessage } }
            : m,
        ),
      })),
    );
  }, []);

  const confirmMutation = useCallback(async () => {
    if (!pendingAction || mutationLoading) return;
    setMutationLoading(true);

    const { toolId, payload, messageId } = pendingAction;

    const result = await executeMutation({
      toolId,
      payload,
      appScope,
      confirmedByUser: true,
    });

    if (isMountedRef.current) {
      _updateProposalOutcome(
        messageId,
        result.ok ? "success" : "error",
        result.userMessage,
      );
      setPendingAction(null);
      setMutationLoading(false);

      // Refresh the operational snapshot so the next message sees updated data.
      // Also emit MUTATION_SUCCESS so liveEventBridge subscribers get instant notification.
      if (result.ok) {
        emitKeviaEvent(KEVIA_EVENTS.MUTATION_SUCCESS, { toolId, ts: Date.now() });
        // Note: the liveEventBridge subscription above will also reload the snapshot,
        // but we keep the direct reload here as a reliable fallback.
        invalidateOperationalSnapshot(appScope);
        loadOperationalSnapshot(appScope).then((snap) => {
          if (isMountedRef.current) setOpSnapshot(snap);
        });
      }
    }
  }, [pendingAction, mutationLoading, appScope, _updateProposalOutcome]);

  const cancelMutation = useCallback(() => {
    if (!pendingAction) return;
    _updateProposalOutcome(pendingAction.messageId, "cancelled", null);
    setPendingAction(null);
  }, [pendingAction, _updateProposalOutcome]);

  return {
    messages,
    loading,
    streamingContent,
    isStreaming,
    sendMessage,
    stopStream,
    retryLast,
    clearConversation,

    // FASE 21
    pendingAction,
    mutationLoading,
    confirmMutation,
    cancelMutation,

    conversations,
    activeConversationId: activeId,
    createConversation,
    selectConversation,
    deleteConversation,
  };
}
